"use client";

import { useState, useEffect, useMemo, useCallback, CSSProperties } from "react";
import Image from "next/image";
import BookingSummary, { BookingSummaryProps } from "./BookingSummary";
import { useGetPractitionersBySlotQuery } from "@/redux/features/publicCalender/publicCalender";
import { ServiceTheme } from "./BookingStepper";
import { formatPractitionerTypes } from "@/lib/utils";

// Brand colors matching BookingStepper theme colors
const themePrimaryColors: Record<ServiceTheme, string> = {
  counseling: "#896deb",
  lifeCoaching: "#DDA500",
  occupationalTherapy: "#C01610",
  psychology: "#C01610",
};

// Default blue theme (used when no service is selected yet)
const defaultThemePrimary = "#006DFF";

const hexToRgba = (hex: string, alpha: number) => {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface PractitionerFiltered {
  id: string;
  counselorId: string; // Same as id, for consistency with Practitioner interface
  name: string;
  role: string;
  image?: string;
  slotId: string; // The specific slot ID for this practitioner
  slotType: string;
}

interface PractitionerSelectionFilteredProps {
  title?: string;
  subtitle?: string;
  defaultSelectedId?: string;
  onPractitionerSelect?: (practitioner: PractitionerFiltered) => void;
  bookingSummary?: Omit<BookingSummaryProps, "practitionerName" | "practitionerImage">;
  // Date-first flow props
  date: string; // YYYY-MM-DD
  startTime: string;
  endTime: string;
  // All unique time ranges for querying practitioners across different time formats
  allTimeRanges?: { start_time: string; end_time: string }[];
  divisionId: string;
  serviceId?: string;
  sessionType?: string;
  serviceTheme?: ServiceTheme;
}

interface BackendPractitioner {
  id: string;
  name: string;
  role: string;
  practitioner_type?: string[];
  specialization?: string;
  profile_picture?: string | null;
  approved_by_admin: boolean;
  slot_id: string;
  slot_type: string;
}

// Convert backend practitioner to frontend format
const convertPractitioner = (p: BackendPractitioner): PractitionerFiltered => ({
  id: p.id,
  counselorId: p.id, // Same as id, for consistency with Practitioner interface
  name: p.name,
  role: p.specialization || formatPractitionerTypes(p.practitioner_type) || "Practitioner",
  image: p.profile_picture || undefined,
  slotId: p.slot_id,
  slotType: p.slot_type,
});

// Practitioner Card Component
const PractitionerCard = ({
  practitioner,
  isSelected,
  onSelect,
  themeColor,
}: {
  practitioner: PractitionerFiltered;
  isSelected: boolean;
  onSelect: () => void;
  themeColor: string;
}) => {
  const imageSrc = practitioner.image || "/images/v3/booking/practionerSelection/practitioner-placeholder.jpg";

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      aria-label={`Select ${practitioner.name}`}
      className={`
        group relative h-[280px] w-full overflow-hidden rounded-[20px] border
        text-left transition-all duration-300 ease-out sm:h-[400px]
        focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
        active:scale-[0.99]
        ${isSelected ? "scale-[1.02]" : "hover:scale-[1.02] hover:shadow-lg"}
      `}
      style={{
        borderColor: hexToRgba(themeColor, 0.2),
        boxShadow: isSelected ? `0px 0px 0px 10px ${hexToRgba(themeColor, 0.3)}` : undefined,
        "--tw-ring-color": themeColor,
      } as CSSProperties}
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <Image
          src={imageSrc}
          alt=""
          fill
          className="-translate-y-[10px] object-cover object-top sm:translate-y-0 sm:object-center transition-transform duration-700 ease-out group-hover:scale-110"
          sizes="(max-width: 768px) 100vw, 492px"
        />
      </div>

      {/* Gradient Overlay with Blur */}
      <div
        className="absolute inset-x-0 bottom-0 rounded-[20px]"
        style={{
          paddingTop: "80px",
          background:
            "linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.2) 10%, rgba(0, 0, 0, 0.45) 25%, rgba(0, 0, 0, 0.6) 45%, rgba(0, 0, 0, 0.72) 70%, rgba(0, 0, 0, 0.82) 100%)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          maskImage: "linear-gradient(to bottom, transparent 0%, black 30%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 30%)",
        }}
      />

      {/* Selection Indicator - Checkmark */}
      <div
        className={`absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-white/30 backdrop-blur-sm transition-all duration-1000 md:h-10 md:w-10 ${
          isSelected
            ? "scale-100 opacity-100 animate-[pulse_6s_ease-in-out_infinite]"
            : "scale-75 opacity-0"
        }`}
      >
        <svg
          className="h-4 w-4 text-white md:h-5 md:w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={3}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-4 md:gap-3.5 md:p-[30px]">

        {/* Name & Role */}
        <div className="flex flex-col">
          <span className="px-1.5 py-1.5 font-onest text-lg font-bold leading-[1.1] text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.9),0_1px_3px_rgba(0,0,0,0.8)] md:px-2.5 md:py-2.5 md:text-2xl">
            {practitioner.name}
          </span>
          <span className="px-1.5 py-1.5 font-onest text-sm font-semibold leading-[1.1] text-white [text-shadow:0_2px_8px_rgba(0,0,0,0.9),0_1px_3px_rgba(0,0,0,0.8)] md:px-2.5 md:py-2.5 md:text-base">
            {practitioner.role}
          </span>
        </div>
      </div>
    </button>
  );
};

// Main Component
const PractitionerSelectionFiltered = ({
  title = "Choose Your Practitioner",
  subtitle = "These practitioners are available for your selected date and time.",
  defaultSelectedId,
  onPractitionerSelect,
  bookingSummary,
  date,
  startTime,
  endTime,
  allTimeRanges,
  divisionId,
  serviceId,
  sessionType,
  serviceTheme,
}: PractitionerSelectionFilteredProps) => {
  const themeColor = serviceTheme ? themePrimaryColors[serviceTheme] : defaultThemePrimary;
  const [selectedId, setSelectedId] = useState<string | null>(
    defaultSelectedId || null
  );
  const [additionalPractitioners, setAdditionalPractitioners] = useState<PractitionerFiltered[]>([]);
  const [isLoadingAdditional, setIsLoadingAdditional] = useState(false);

  // Fetch practitioners available for the selected slot (primary query)
  const {
    data: practitionersResponse,
    isLoading,
    error,
  } = useGetPractitionersBySlotQuery(
    {
      date,
      start_time: startTime,
      end_time: endTime,
      division_id: divisionId,
      ...(serviceId && { service_id: serviceId }),
      ...(sessionType && { session_type: sessionType }),
    },
    { skip: !date || !startTime || !endTime || !divisionId }
  );

  // Fetch additional practitioners for other time ranges
  const fetchAdditionalPractitioners = useCallback(async () => {
    if (!allTimeRanges || allTimeRanges.length <= 1 || !date || !divisionId) {
      setAdditionalPractitioners([]);
      return;
    }

    // Skip the first range as it's handled by the primary query
    const additionalRanges = allTimeRanges.slice(1);
    if (additionalRanges.length === 0) {
      setAdditionalPractitioners([]);
      return;
    }

    setIsLoadingAdditional(true);
    try {
      const baseUrl = process.env.NEXT_PUBLIC_BASEURL || '';
      const results = await Promise.all(
        additionalRanges.map(async (range) => {
          const params = new URLSearchParams();
          params.set('date', date);
          params.set('start_time', range.start_time);
          params.set('end_time', range.end_time);
          params.set('division_id', divisionId);
          if (serviceId) params.set('service_id', serviceId);
          if (sessionType) params.set('session_type', sessionType);

          const response = await fetch(`${baseUrl}/public-calenders/practitioners-by-slot?${params}`);
          if (!response.ok) return [];
          const data = await response.json();
          return data?.data?.practitioners || [];
        })
      );

      // Flatten and convert practitioners
      const allAdditional = results.flat().map(convertPractitioner);
      setAdditionalPractitioners(allAdditional);
    } catch (err) {
      console.error("Error fetching additional practitioners:", err);
      setAdditionalPractitioners([]);
    } finally {
      setIsLoadingAdditional(false);
    }
  }, [allTimeRanges, date, divisionId, serviceId, sessionType]);

  // Fetch additional practitioners when allTimeRanges changes
  useEffect(() => {
    fetchAdditionalPractitioners();
  }, [fetchAdditionalPractitioners]);

  // Convert API response to practitioners and merge with additional
  const practitioners: PractitionerFiltered[] = useMemo(() => {
    const primaryPractitioners = practitionersResponse?.data?.practitioners
      ? practitionersResponse.data.practitioners.map(convertPractitioner)
      : [];

    // Merge and deduplicate by practitioner ID
    const allPractitioners = [...primaryPractitioners, ...additionalPractitioners];
    const uniqueMap = new Map<string, PractitionerFiltered>();
    for (const p of allPractitioners) {
      if (!uniqueMap.has(p.id)) {
        uniqueMap.set(p.id, p);
      }
    }
    return Array.from(uniqueMap.values());
  }, [practitionersResponse, additionalPractitioners]);

  // Reset selection when practitioners change
  useEffect(() => {
    if (defaultSelectedId) {
      setSelectedId(defaultSelectedId);
    }
  }, [defaultSelectedId, date, startTime]);

  const handleSelect = (practitioner: PractitionerFiltered) => {
    setSelectedId(practitioner.id);
    onPractitionerSelect?.(practitioner);
  };

  const selectedPractitioner = practitioners.find((p) => p.id === selectedId);

  // Loading state
  if (isLoading || isLoadingAdditional) {
    return (
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
        <div
          className={`flex flex-col gap-6 md:gap-10 ${bookingSummary ? "lg:w-[60%]" : "lg:w-full"}`}
        >
          <div className="flex flex-col">
            <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
              {title}
            </h2>
            <p className="max-w-[565px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
              {subtitle}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-[350px] animate-pulse rounded-[20px] bg-gray-200 sm:h-[400px] md:h-[548px]"
              />
            ))}
          </div>
        </div>
        {bookingSummary && (
          <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:w-[40%] lg:shrink-0">
            <BookingSummary
              {...bookingSummary}
              practitionerName={null}
              practitionerImage={null}
              serviceTheme={serviceTheme}
            />
          </div>
        )}
      </div>
    );
  }

  // Error state
  if (error) {
    console.error("Failed to fetch practitioners:", error);
  }

  // No practitioners available
  if (practitioners.length === 0) {
    return (
      <div className="flex flex-col gap-6 md:gap-10">
        <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
          <div className="flex flex-1 flex-col gap-4 py-4 md:gap-[30px] md:py-[39px]">
            <div className="flex flex-col">
              <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
                {title}
              </h2>
              <p className="max-w-[565px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
                {subtitle}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center rounded-[20px] bg-gray-100 p-10">
          <p className="text-center font-onest text-lg text-gray-600">
            No practitioners available for the selected time slot. Please go back
            and select a different date or time.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:gap-10">
      {/* Left Side: Header + Avatar Stack + Practitioner Cards */}
      <div
        className={`flex flex-col gap-6 md:gap-10 ${bookingSummary ? "lg:w-[60%]" : "lg:w-full"}`}
      >
        {/* Header */}
        <div className="flex flex-col gap-4 md:gap-[30px]">
          <div className="flex flex-col">
            <h2 className="p-2.5 font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
              {title}
            </h2>
            <p className="max-w-[565px] p-2.5 font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
              {subtitle}
            </p>
          </div>
        </div>

        {/* Practitioner Cards */}
        <div
          className="grid grid-cols-1 gap-5 md:grid-cols-2"
          role="radiogroup"
          aria-label="Available practitioners"
        >
          {practitioners.map((practitioner) => (
            <PractitionerCard
              key={practitioner.id}
              practitioner={practitioner}
              isSelected={selectedId === practitioner.id}
              onSelect={() => handleSelect(practitioner)}
              themeColor={themeColor}
            />
          ))}
        </div>
      </div>

      {/* Right Side: Booking Summary */}
      {bookingSummary && (
        <div className="w-full rounded-[10px] bg-white p-4 md:p-[30px] lg:w-[40%] lg:shrink-0">
          <BookingSummary
            {...bookingSummary}
            practitionerName={selectedPractitioner?.name || null}
            practitionerImage={selectedPractitioner?.image || null}
            serviceTheme={serviceTheme}
          />
        </div>
      )}
    </div>
  );
};

export default PractitionerSelectionFiltered;
